import React, { useState } from 'react';
import { Box, Divider, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import defaultArticleThumbnail from '../../assets/default_article_thumbnail.svg';
import NewsSourceWithDate from '../common/NewsSourceWithDate';
import CommentCount from '../common/CommentCount';
import BookmarkCount from '../common/BookmarkCount';
import CardTitle from '../common/CardTitle';

const NewsCard = ({
  news,
  imageMaxWidth = 220,
  showContent = false,
  actionButton,
  referrerType,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const [imageError, setImageError] = useState(false);
  const newsUrl = `/news/${news.slug || news.id}`;

  const handleLinkToNews = (e) => {
    if (!e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      navigateWithScrollMemory(newsUrl, referrerType ? { state: { referrerType } } : undefined);
    }
  };

  const handleError = () => {
    if (!imageError) setImageError(true);
  };

  const sourceFontSize = { xs: 13, md: 14, lg: 14 };
  const dateFontSize = 12;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        gap: { xs: '10px', md: '16px', lg: '20px' },
      }}
    >
      {/* 이미지 */}
      <Box
        component={RouterLink}
        to={newsUrl}
        state={referrerType ? { referrerType } : undefined}
        onClick={handleLinkToNews}
        sx={{
          display: 'block',
          width: { xs: '34%', md: '20%', lg: '25%' },
          minWidth: { xs: 112, md: 168 },
          maxWidth: { md: imageMaxWidth },
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={imageError || !news.imageUrl ? defaultArticleThumbnail : news.imageUrl}
          alt={imageError || !news.imageUrl ? 'Default article thumbnail' : news.title}
          onError={handleError}
          sx={{
            width: '100%',
            aspectRatio: '280 / 188',
            cursor: 'pointer',
            objectFit: 'cover',
            display: 'block',
            border: `1px solid ${theme.palette.grey.line}`,
          }}
        />
      </Box>

      {/* 정보 영역 */}
      <Box
        sx={{
          flex: 1,
          width: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {/* 제목 */}
        <CardTitle
          title={news.title}
          to={newsUrl}
          onClick={handleLinkToNews}
          sx={{ lineHeight: { xs: 1.35, md: 1.5 } }}
        />

        {/* 하단 정보 + actionButton */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
          }}
        >
          {/* 좌측: 출처/날짜/댓글/북마크 */}
          <Box>
            {/* 레이아웃 A (md+): 출처|날짜 / 댓글 북마크 */}
            {!isXs && (
              <>
                <NewsSourceWithDate
                  linkUrl={news.linkUrl}
                  source={news.sourceUrl}
                  date={news.releaseDate}
                  fontSize={sourceFontSize}
                  sx={{ mt: { md: '12px', lg: '16px' } }}
                />
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ mt: { md: '12px', lg: '16px' } }}
                >
                  <CommentCount commentCount={news.commentCount} />
                  <BookmarkCount bookmarkCount={news.bookmarkCount} />
                </Stack>
              </>
            )}

            {/* 레이아웃 B (xs): 출처 / 날짜|댓글 북마크 */}
            {isXs && (
              <>
                <NewsSourceWithDate
                  linkUrl={news.linkUrl}
                  source={news.sourceUrl}
                  fontSize={sourceFontSize}
                  sx={{ mt: '8px' }}
                />
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: '8px' }}>
                  {news.releaseDate && (
                    <>
                      <Typography
                        fontSize={dateFontSize}
                        sx={{ color: theme.palette.black[4], whiteSpace: 'nowrap' }}
                      >
                        {news.releaseDate}
                      </Typography>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                          height: '8px',
                          alignSelf: 'center',
                          backgroundColor: theme.palette.grey.line,
                        }}
                      />
                    </>
                  )}
                  <CommentCount commentCount={news.commentCount} />
                  <BookmarkCount bookmarkCount={news.bookmarkCount} />
                </Stack>
              </>
            )}
          </Box>

          {/* 우측: actionButton */}
          {actionButton}
        </Box>

        {/* 본문 */}
        {showContent && (
          <Typography
            fontSize={{ xs: 13, md: 14, lg: 15 }}
            color={theme.palette.black[6]}
            sx={{
              display: { xs: 'none', md: '-webkit-box' },
              mt: { md: '12px', lg: '16px' },
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word',
              lineHeight: 1.5,
            }}
          >
            {news.content}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default NewsCard;
